/**
 * Frontend Permission Constants
 * 
 * Mirror of backend permission registry for use in React components.
 * Import from this file to check permissions in UI.
 * 
 * Usage:
 * ```tsx
 * import { PERMISSIONS } from '@/constants/permissions';
 * 
 * <PermissionGate permission={PERMISSIONS.MANAGE_USERS}>
 *   <Button>Invite User</Button>
 * </PermissionGate>
 * ```
 */

export const PERMISSIONS = {
  // ===== USER MANAGEMENT =====
  MANAGE_USERS: 'manage_users',
  INVITE_USERS: 'invite_users',
  REMOVE_USERS: 'remove_users',
  EDIT_USER_ROLES: 'edit_user_roles',
  VIEW_USERS: 'view_users',

  // ===== ROLE & PERMISSION MANAGEMENT =====
  MANAGE_ROLES: 'manage_roles',

  // ===== VENUES =====
  MANAGE_VENUES: 'manage_venues',
  CREATE_VENUES: 'create_venues',
  READ_VENUES: 'read_venues',
  UPDATE_VENUES: 'update_venues',
  DELETE_VENUES: 'delete_venues',

  // ===== CLIENTS =====
  MANAGE_CLIENTS: 'manage_clients',
  CREATE_CLIENTS: 'create_clients',
  READ_CLIENTS: 'read_clients',
  UPDATE_CLIENTS: 'update_clients',
  DELETE_CLIENTS: 'delete_clients',

  // ===== EVENTS =====
  MANAGE_EVENTS: 'manage_events',
  CREATE_EVENTS: 'create_events',
  READ_EVENTS: 'read_events',
  UPDATE_EVENTS: 'update_events',
  DELETE_EVENTS: 'delete_events',

  // ===== ATTENDEES =====
  MANAGE_ATTENDEES: 'manage_attendees',
  CREATE_ATTENDEES: 'create_attendees',
  READ_ATTENDEES: 'read_attendees',
  UPDATE_ATTENDEES: 'update_attendees',
  DELETE_ATTENDEES: 'delete_attendees',
  IMPORT_ATTENDEES: 'import_attendees',

  // ===== ASSESSMENTS =====
  MANAGE_ASSESSMENTS: 'manage_assessments',
  CREATE_ASSESSMENTS: 'create_assessments',
  READ_ASSESSMENTS: 'read_assessments',
  UPDATE_ASSESSMENTS: 'update_assessments',
  DELETE_ASSESSMENTS: 'delete_assessments',

  // ===== ATTACHMENTS =====
  MANAGE_ATTACHMENTS: 'manage_attachments',

  // ===== ORGANIZATION KNOWLEDGE =====
  MANAGE_KNOWLEDGE: 'manage_knowledge',

  // ===== ORGANIZATION SETTINGS =====
  MANAGE_ORGANIZATION_SETTINGS: 'manage_organization_settings',

  // ===== DESTINATION LIBRARY =====
  MANAGE_DESTINATIONS: 'manage_destinations',
} as const;

/**
 * Permission type
 */
export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

/**
 * Permission metadata for UI
 */
export interface PermissionMetadata {
  label: string;
  description: string;
  category: string;
  granularOf?: Permission;
}

/**
 * Permission metadata for display in UI
 */
export const PERMISSION_METADATA: Record<Permission, PermissionMetadata> = {
  // User Management
  [PERMISSIONS.MANAGE_USERS]: {
    label: 'Manage Users',
    description: 'Full access to invite, remove, and manage organization members',
    category: 'User Management',
  },
  [PERMISSIONS.INVITE_USERS]: {
    label: 'Invite Users',
    description: 'Send invitations to new members',
    category: 'User Management',
    granularOf: PERMISSIONS.MANAGE_USERS,
  },
  [PERMISSIONS.REMOVE_USERS]: {
    label: 'Remove Users',
    description: 'Remove members from the organization',
    category: 'User Management',
    granularOf: PERMISSIONS.MANAGE_USERS,
  },
  [PERMISSIONS.EDIT_USER_ROLES]: {
    label: 'Edit User Roles',
    description: 'Change member roles and permissions',
    category: 'User Management',
    granularOf: PERMISSIONS.MANAGE_USERS,
  },
  [PERMISSIONS.VIEW_USERS]: {
    label: 'View Users',
    description: 'View the member list and their details',
    category: 'User Management',
    granularOf: PERMISSIONS.MANAGE_USERS,
  },

  // Role Management
  [PERMISSIONS.MANAGE_ROLES]: {
    label: 'Manage Roles',
    description: 'Create, edit, and delete custom roles and permissions',
    category: 'Role Management',
  },

  // Venues
  [PERMISSIONS.MANAGE_VENUES]: {
    label: 'Manage Venues',
    description: 'Full access to create, view, edit, and delete venues',
    category: 'Venues',
  },
  [PERMISSIONS.CREATE_VENUES]: {
    label: 'Create Venues',
    description: 'Add new venues to the database',
    category: 'Venues',
    granularOf: PERMISSIONS.MANAGE_VENUES,
  },
  [PERMISSIONS.READ_VENUES]: {
    label: 'View Venues',
    description: 'View venue information and details',
    category: 'Venues',
    granularOf: PERMISSIONS.MANAGE_VENUES,
  },
  [PERMISSIONS.UPDATE_VENUES]: {
    label: 'Edit Venues',
    description: 'Modify existing venue information',
    category: 'Venues',
    granularOf: PERMISSIONS.MANAGE_VENUES,
  },
  [PERMISSIONS.DELETE_VENUES]: {
    label: 'Delete Venues',
    description: 'Remove venues from the database',
    category: 'Venues',
    granularOf: PERMISSIONS.MANAGE_VENUES,
  },

  // Clients
  [PERMISSIONS.MANAGE_CLIENTS]: {
    label: 'Manage Clients',
    description: 'Full access to create, view, edit, and delete clients',
    category: 'Clients',
  },
  [PERMISSIONS.CREATE_CLIENTS]: {
    label: 'Create Clients',
    description: 'Add new clients',
    category: 'Clients',
    granularOf: PERMISSIONS.MANAGE_CLIENTS,
  },
  [PERMISSIONS.READ_CLIENTS]: {
    label: 'View Clients',
    description: 'View client information',
    category: 'Clients',
    granularOf: PERMISSIONS.MANAGE_CLIENTS,
  },
  [PERMISSIONS.UPDATE_CLIENTS]: {
    label: 'Edit Clients',
    description: 'Modify existing client information',
    category: 'Clients',
    granularOf: PERMISSIONS.MANAGE_CLIENTS,
  },
  [PERMISSIONS.DELETE_CLIENTS]: {
    label: 'Delete Clients',
    description: 'Remove clients',
    category: 'Clients',
    granularOf: PERMISSIONS.MANAGE_CLIENTS,
  },

  // Events
  [PERMISSIONS.MANAGE_EVENTS]: {
    label: 'Manage Events',
    description: 'Full access to create, view, edit, and delete events',
    category: 'Events',
  },
  [PERMISSIONS.CREATE_EVENTS]: {
    label: 'Create Events',
    description: 'Add new events',
    category: 'Events',
    granularOf: PERMISSIONS.MANAGE_EVENTS,
  },
  [PERMISSIONS.READ_EVENTS]: {
    label: 'View Events',
    description: 'View event information',
    category: 'Events',
    granularOf: PERMISSIONS.MANAGE_EVENTS,
  },
  [PERMISSIONS.UPDATE_EVENTS]: {
    label: 'Edit Events',
    description: 'Modify existing event information',
    category: 'Events',
    granularOf: PERMISSIONS.MANAGE_EVENTS,
  },
  [PERMISSIONS.DELETE_EVENTS]: {
    label: 'Delete Events',
    description: 'Remove events',
    category: 'Events',
    granularOf: PERMISSIONS.MANAGE_EVENTS,
  },

  // Attendees
  [PERMISSIONS.MANAGE_ATTENDEES]: {
    label: 'Manage Attendees',
    description: 'Full access to create, view, edit, delete, and import attendees',
    category: 'Attendees',
  },
  [PERMISSIONS.CREATE_ATTENDEES]: {
    label: 'Create Attendees',
    description: 'Add new attendees',
    category: 'Attendees',
    granularOf: PERMISSIONS.MANAGE_ATTENDEES,
  },
  [PERMISSIONS.READ_ATTENDEES]: {
    label: 'View Attendees',
    description: 'View attendee information',
    category: 'Attendees',
    granularOf: PERMISSIONS.MANAGE_ATTENDEES,
  },
  [PERMISSIONS.UPDATE_ATTENDEES]: {
    label: 'Edit Attendees',
    description: 'Modify existing attendee information',
    category: 'Attendees',
    granularOf: PERMISSIONS.MANAGE_ATTENDEES,
  },
  [PERMISSIONS.DELETE_ATTENDEES]: {
    label: 'Delete Attendees',
    description: 'Remove attendees',
    category: 'Attendees',
    granularOf: PERMISSIONS.MANAGE_ATTENDEES,
  },
  [PERMISSIONS.IMPORT_ATTENDEES]: {
    label: 'Import Attendees',
    description: 'Import attendees from CSV files',
    category: 'Attendees',
    granularOf: PERMISSIONS.MANAGE_ATTENDEES,
  },

  // Assessments
  [PERMISSIONS.MANAGE_ASSESSMENTS]: {
    label: 'Manage Assessments',
    description: 'Full access to assessments (flight cost estimator, etc.)',
    category: 'Assessments',
  },
  [PERMISSIONS.CREATE_ASSESSMENTS]: {
    label: 'Create Assessments',
    description: 'Create new assessments',
    category: 'Assessments',
    granularOf: PERMISSIONS.MANAGE_ASSESSMENTS,
  },
  [PERMISSIONS.READ_ASSESSMENTS]: {
    label: 'View Assessments',
    description: 'View assessment results',
    category: 'Assessments',
    granularOf: PERMISSIONS.MANAGE_ASSESSMENTS,
  },
  [PERMISSIONS.UPDATE_ASSESSMENTS]: {
    label: 'Edit Assessments',
    description: 'Modify assessment parameters',
    category: 'Assessments',
    granularOf: PERMISSIONS.MANAGE_ASSESSMENTS,
  },
  [PERMISSIONS.DELETE_ASSESSMENTS]: {
    label: 'Delete Assessments',
    description: 'Remove assessments',
    category: 'Assessments',
    granularOf: PERMISSIONS.MANAGE_ASSESSMENTS,
  },

  // Attachments
  [PERMISSIONS.MANAGE_ATTACHMENTS]: {
    label: 'Manage Attachments',
    description: 'Full access to upload, view, and delete organization attachments',
    category: 'Organization Content',
  },

  // Organization Knowledge
  [PERMISSIONS.MANAGE_KNOWLEDGE]: {
    label: 'Manage Company Knowledge',
    description: 'Full access to upload, view, and delete company knowledge documents',
    category: 'Organization Content',
  },

  // Organization Settings
  [PERMISSIONS.MANAGE_ORGANIZATION_SETTINGS]: {
    label: 'Manage Organization Settings',
    description: 'Full access to manage organization profile, brand guidelines, and settings',
    category: 'Organization Settings',
  },

  // Destination Library
  [PERMISSIONS.MANAGE_DESTINATIONS]: {
    label: 'Manage Destinations',
    description: 'Full access to create, view, edit, and delete research destinations',
    category: 'Destination Library',
  },
};

/**
 * Get permissions grouped by category for UI display
 */
export function getPermissionsByCategory(): Record<string, Permission[]> {
  const grouped: Record<string, Permission[]> = {};

  for (const [permission, metadata] of Object.entries(PERMISSION_METADATA)) {
    const category = metadata.category;
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(permission as Permission);
  }

  return grouped;
}

/**
 * Format permission string to human-readable label
 */
export function formatPermissionLabel(permission: string): string {
  return PERMISSION_METADATA[permission as Permission]?.label || permission;
}

